import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generateId } from '@/utils/helpers';


export const useChat = (gpcService, onSessionExpired) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [totalConversations, setTotalConversations] = useState(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isRestoringConversation, setIsRestoringConversation] = useState(true);
  const [conversationsOffset, setConversationsOffset] = useState(0);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [isLoadingMoreConversations, setIsLoadingMoreConversations] = useState(false);
  const [messagesOffset, setMessagesOffset] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);

  const sendMessage = useCallback(
    async (content) => {
      if (!content.trim() || isLoading) {
        return;
      }

      const userMessage = {
        id: generateId(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date().toISOString()
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      const tempMessageId = generateId();
      const tempMessage = {
        id: tempMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        isStreaming: true
      };

      setMessages((prev) => [...prev, tempMessage]);

      try {
        const response = await gpcService.sendMessage(
          content,
          (chunk) => {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === tempMessageId
                  ? { ...msg, content: msg.content + chunk }
                  : msg
              )
            );
          },
          currentConversationId
        );

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempMessageId
              ? {
                id: response.data.message_id,
                role: 'assistant',
                content: response.data.response,
                timestamp: response.data.created_at,
                isStreaming: false,
                metadata: {
                  responseTimeMs: response.data.response_time_ms,
                  modelUsed: response.data.model_used
                }
              }
              : msg
          )
        );

        if (response.data.conversation_id) {
          setCurrentConversationId(response.data.conversation_id);
        }

        loadConversations();
      } catch (err) {
        setMessages((prev) => prev.filter((msg) => msg.id !== tempMessageId));

        let errorData;
        try {
          errorData = JSON.parse(err.message);
        } catch {
          errorData = { statusCode: 500, message: err.message };
        }

        let errorMessage;
        const statusCode = errorData.statusCode || 500;
        const errorCode = errorData.errorCode || '';

        if (statusCode === 401 && errorCode === 'SESSION_EXPIRED') {
          errorMessage = t('toast.sessionExpired');
          if (onSessionExpired) {
            onSessionExpired();
          }
        }

        switch (statusCode) {
          case 401:
            if (!errorMessage) {
              errorMessage = t('errors.unauthorized');
            }
            break;
          case 402:
            errorMessage = t('errors.insufficientCredits');
            break;
          case 403:
            if (errorCode.includes('DOMAIN') || errorCode.includes('UNAUTHORIZED')) {
              errorMessage = t('errors.domainUnauthorized');
            } else {
              errorMessage = t('errors.validation');
            }
            break;
          case 422:
            if (errorCode === 'LOW_RELEVANCE_SCORE' && errorData.message) {
              errorMessage = errorData.message;
            } else {
              errorMessage = t('errors.noKnowledgeAvailable');
            }
            break;
          case 429:
            errorMessage = t('errors.rateLimit');
            break;
          case 503:
          case 502:
          case 504:
            errorMessage = t('errors.aiServiceUnavailable');
            break;
          case 500:
            errorMessage = t('errors.serverError');
            break;
          default:
            errorMessage = t('errors.generic');
        }

        setError(errorMessage);

        const errorMsg = {
          id: generateId(),
          role: 'assistant',
          content: errorMessage,
          timestamp: new Date().toISOString()
        };

        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsLoading(false);
      }
    },
    [gpcService, isLoading, t, onSessionExpired, currentConversationId]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const loadConversationHistory = useCallback(async (conversationId, resetPagination = true) => {
    if (!gpcService || !conversationId) return;

    setIsLoadingHistory(true);
    try {
      const offset = resetPagination ? 0 : messagesOffset;
      const response = await gpcService.getConversationHistory(conversationId, 50, offset);

      if (response.success && response.data.messages) {
        const formattedMessages = response.data.messages.flatMap(msg => [
          {
            id: `${msg.message_id}-question`,
            role: 'user',
            content: msg.question,
            timestamp: msg.creation_date
          },
          {
            id: msg.message_id,
            role: 'assistant',
            content: msg.answer,
            timestamp: msg.creation_date,
            metadata: {
              modelUsed: msg.model
            }
          }
        ]);

        if (resetPagination) {
          setMessages(formattedMessages);
          setMessagesOffset(response.data.count);
        } else {
          setMessages(prev => [...formattedMessages, ...prev]);
          setMessagesOffset(prev => prev + response.data.count);
        }

        setHasMoreMessages(response.data.has_more);
        setCurrentConversationId(conversationId);
      }
    } catch (err) {
      console.error('Error loading conversation history:', err);
      setMessages(prev => [...prev, { id: generateId(), role: 'assistant', content: t('toast.failedLoadHistory'), timestamp: new Date().toISOString() }]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [gpcService, t, messagesOffset]);

  const loadConversations = useCallback(async (resetPagination = true) => {
    if (!gpcService) return;

    try {
      const offset = resetPagination ? 0 : conversationsOffset;
      const response = await gpcService.listConversations(20, offset);

      if (response.success && response.data.conversations) {
        if (resetPagination) {
          setConversations(response.data.conversations);
          setConversationsOffset(response.data.count);
        } else {
          setConversations(prev => [...prev, ...response.data.conversations]);
          setConversationsOffset(prev => prev + response.data.count);
        }
        setHasMoreConversations(response.data.has_more);
        setTotalConversations(response.data.total || 0);
      }
    } catch (err) {
      console.error('Error loading conversations:', err);
    }
  }, [gpcService, conversationsOffset]);

  const loadMoreConversations = useCallback(async () => {
    if (!gpcService || isLoadingMoreConversations || !hasMoreConversations) return;

    setIsLoadingMoreConversations(true);
    try {
      await loadConversations(false);
    } finally {
      setIsLoadingMoreConversations(false);
    }
  }, [gpcService, isLoadingMoreConversations, hasMoreConversations, loadConversations]);

  const loadMoreMessages = useCallback(async () => {
    if (!gpcService || !currentConversationId || isLoadingMoreMessages || !hasMoreMessages) return;

    setIsLoadingMoreMessages(true);
    try {
      await loadConversationHistory(currentConversationId, false);
    } finally {
      setIsLoadingMoreMessages(false);
    }
  }, [gpcService, currentConversationId, isLoadingMoreMessages, hasMoreMessages, loadConversationHistory]);

  const startNewConversation = useCallback(() => {
    setMessages([]);
    setCurrentConversationId(null);
    setError(null);
    if (gpcService) {
      gpcService.clearLastConversation();
    }
  }, [t, gpcService]);

  const switchConversation = useCallback(async (conversationId) => {
    await loadConversationHistory(conversationId);
  }, [loadConversationHistory]);

  const renameConversation = useCallback(async (conversationId, newTitle) => {
    if (!gpcService) return;

    try {
      await gpcService.updateConversationTitle(conversationId, newTitle);
      await loadConversations(true);
    } catch (err) {
      console.error('Error renaming conversation:', err);
      setMessages(prev => [...prev, { id: generateId(), role: 'assistant', content: t('toast.failedRenameConversation'), timestamp: new Date().toISOString() }]);
    }
  }, [gpcService, loadConversations, t]);

  const deleteConversation = useCallback(async (conversationId) => {
    if (!gpcService) return;

    try {
      await gpcService.deleteConversation(conversationId);

      if (conversationId === currentConversationId) {
        setMessages([]);
        setCurrentConversationId(null);
      }

      await loadConversations(true);
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setMessages(prev => [...prev, { id: generateId(), role: 'assistant', content: t('toast.failedDeleteConversation'), timestamp: new Date().toISOString() }]);
    }
  }, [gpcService, currentConversationId, loadConversations, t]);

  useEffect(() => {
    const restoreLastConversation = async () => {
      if (!gpcService) {
        setIsRestoringConversation(false);
        return;
      }

      try {
        const lastConversation = gpcService.getLastConversation();

        if (lastConversation) {
          setMessages(lastConversation.messages);
          setCurrentConversationId(lastConversation.conversationId);
        }
      } catch (error) {
        console.error('[TIA GPC] Error restaurando conversación:', error);
      } finally {
        setIsRestoringConversation(false);
      }
    };

    restoreLastConversation();
  }, [gpcService]);

  useEffect(() => {
    if (gpcService && !isRestoringConversation) {
      loadConversations();
    }
  }, [gpcService, loadConversations, isRestoringConversation]);

  useEffect(() => {
    if (gpcService && messages.length > 0 && !isRestoringConversation) {
      gpcService.saveLastConversation(currentConversationId, messages);
    }
  }, [gpcService, messages, currentConversationId, isRestoringConversation]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    clearError,
    currentConversationId,
    conversations,
    totalConversations,
    isLoadingHistory,
    loadConversationHistory,
    loadConversations,
    startNewConversation,
    switchConversation,
    renameConversation,
    deleteConversation,
    isRestoringConversation,
    hasMoreConversations,
    isLoadingMoreConversations,
    loadMoreConversations,
    hasMoreMessages,
    isLoadingMoreMessages,
    loadMoreMessages
  };
};